export const clamp = (x, min, max) => Math.max(min, Math.min(max, x));

// Apple's momentum projection: where a flick at `velocity` (px/s) would come to rest.
export function project(velocity, decelerationRate = 0.998) {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate);
}

// Progressive resistance past a boundary: the further over, the less it follows.
export function rubberband(overshoot, dimension, constant = 0.55) {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
}

export function nearestSnap(value, snaps) {
  return snaps.reduce((best, p) => (Math.abs(p - value) < Math.abs(best - value) ? p : best), snaps[0]);
}

// Choose where a released drag should land, using velocity to project the resting position.
export function pickSnap(position, velocity, snaps) {
  return nearestSnap(position + project(velocity), snaps);
}

// Add bounce only when the gesture carried momentum.
export function dampingFor(velocity, bounce = 0.8, threshold = 250) {
  return Math.abs(velocity) > threshold ? bounce : 1;
}

// One integration step of a damped spring (4 substeps for stability).
export function springStep({ x, v }, target, zeta, response, dt) {
  const w = (2 * Math.PI) / response;
  const k = w * w;
  const c = 2 * zeta * w;
  const sub = 4;
  let nx = x;
  let nv = v;
  for (let i = 0; i < sub; i += 1) {
    const a = -k * (nx - target) - c * nv;
    nv += (a * dt) / sub;
    nx += (nv * dt) / sub;
  }
  return { x: nx, v: nv };
}

// Animate a value with a spring, starting from its current value and velocity. Returns a cancel function.
export function runSpring({ from, to, velocity = 0, zeta = 1, response = 0.35, onUpdate, onDone }) {
  let state = { x: from, v: velocity };
  let last = performance.now();
  let raf = 0;
  let dead = false;
  const frame = (now) => {
    if (dead) return;
    const dt = Math.max(0, Math.min(0.034, (now - last) / 1000));
    last = now;
    state = springStep(state, to, zeta, response, dt);
    if (Math.abs(state.x - to) < 0.3 && Math.abs(state.v) < 6) {
      onUpdate(to, 0);
      if (onDone) onDone();
      return;
    }
    onUpdate(state.x, state.v);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);
  return () => {
    dead = true;
    cancelAnimationFrame(raf);
  };
}

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
